import { useCallback } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { StarFilled, StarRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  button: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground3,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase400,
    transitionProperty: 'opacity, background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  /** Hidden by default; revealed on parent `:hover` / `:focus-within`. */
  hiddenUntilHover: {
    opacity: 0,
    ':focus-visible': {
      opacity: 1,
    },
  },
  active: {
    opacity: 1,
    color: tokens.colorBrandForeground1,
    '@media (forced-colors: active)': {
      color: 'Highlight',
    },
  },
});

type FavoriteStarButtonProps = {
  isFavorited: boolean;
  onToggle: () => void;
  /** Accessible name; also rendered as the `title` tooltip. */
  label: string;
  /** Short tooltip override. Defaults to `label`. */
  title?: string;
  /** Extra class applied to the root so parents can target it with `:hover` reveals. */
  revealClass?: string;
  /** When true, the star is hidden until the parent is hovered or focused-within. */
  hiddenUntilHover?: boolean;
};

/**
 * Star toggle for favoriting connectors and operations. Rendered as a
 * `<span role="button">` so it can be nested inside other interactive
 * elements (accordion headers, operation rows) without violating HTML
 * interactive-content rules. Click and key events are stopped so the
 * surrounding control doesn't toggle.
 */
export const FavoriteStarButton = ({
  isFavorited,
  onToggle,
  label,
  title,
  revealClass,
  hiddenUntilHover = false,
}: FavoriteStarButtonProps) => {
  const styles = useStyles();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    },
    [onToggle],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }
    },
    [onToggle],
  );

  return (
    <span
      role="button"
      tabIndex={0}
      className={mergeClasses(
        revealClass,
        styles.button,
        hiddenUntilHover && !isFavorited && styles.hiddenUntilHover,
        isFavorited && styles.active,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={label}
      aria-pressed={isFavorited}
      title={title ?? label}
    >
      {isFavorited ? <StarFilled /> : <StarRegular />}
    </span>
  );
};

export default FavoriteStarButton;
